import { supabase } from '../lib/supabase';
import { allRows, rows } from '../shelves/load';
import type { Precision } from '../book/types';
import type { OwnedCopy, Room } from './owned';

// My books › Owned copies (Part 3): every copy you own, share, have lent, lost or given away
// (never a library copy), with its book's title and first author, its edition's IDs, its store,
// and its private details (room, shelf, price, who has it). As you (owner rules).

type Row = Record<string, unknown>;
const one = <T>(v: unknown): T | null => (Array.isArray(v) ? ((v[0] as T) ?? null) : ((v as T) ?? null));
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

export interface OwnedFacts {
  copies: OwnedCopy[];
  rooms: Room[];
}

const SELECT =
  'id, book_id, format, how, state, acquired_on, acquired_precision, store:stores(name), edition:editions(kind, asin, audible_id, cover_url), ' +
  'priv:copy_private(home_location_id, shelf_name, lent_to, lent_on, price, acquired_words, got_from), ' +
  'book:books!inner(label_number, removed_at, work_id, work:works(title, work_people(role, sort_order, person:people(name))))';

export async function loadOwnedFacts(): Promise<OwnedFacts> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const [list, rooms] = await Promise.all([
    allRows(() => db.from('copies').select(SELECT).neq('how', 'library_loan').order('id') as never),
    rows(db.from('home_locations').select('id, name, sort_order').order('sort_order')),
  ]);
  const copies: OwnedCopy[] = [];
  for (const c of list) {
    const book = one<Row>(c.book);
    if (!book || book.removed_at) continue;
    // A "no format yet" placeholder isn't a copy you can find on a shelf.
    if (!c.format) continue;
    const work = one<Row>(book.work);
    const author =
      ((work?.work_people as Row[]) ?? [])
        .filter((p) => p.role === 'author' || p.role === 'co_author')
        .sort((x, y) => Number(x.sort_order ?? 0) - Number(y.sort_order ?? 0))
        .map((p) => (one<Row>(p.person)?.name as string) ?? '')
        .find(Boolean) ?? '';
    const ed = one<Row>(c.edition);
    const priv = one<Row>(c.priv);
    copies.push({
      id: c.id as string,
      bookId: c.book_id as string,
      workId: (book.work_id as string) ?? '',
      editionCover: str(ed?.cover_url),
      label: Number(book.label_number),
      title: (work?.title as string) ?? '',
      author,
      format: c.format as string,
      listen: (ed?.kind as string) === 'audiobook',
      how: c.how as OwnedCopy['how'],
      state: c.state as OwnedCopy['state'],
      acquiredOn: str(c.acquired_on),
      acquiredPrecision: (c.acquired_precision as Precision | null) ?? null,
      acquiredWords: str(priv?.acquired_words),
      store: str(one<Row>(c.store)?.name),
      price: priv?.price === null || priv?.price === undefined ? null : Number(priv.price),
      roomId: str(priv?.home_location_id),
      shelf: str(priv?.shelf_name),
      lentTo: str(priv?.lent_to),
      lentOn: str(priv?.lent_on),
      gotFrom: str(priv?.got_from),
      asin: str(ed?.asin),
      audibleId: str(ed?.audible_id),
    });
  }
  return {
    copies,
    rooms: rooms.map((r) => ({ id: r.id as string, name: (r.name as string) ?? '', order: Number(r.sort_order ?? 0) })),
  };
}

/** Your rooms, in your order (for Where ▾ on the book page). */
export async function loadRooms(): Promise<Room[]> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const list = await rows(db.from('home_locations').select('id, name, sort_order').order('sort_order'));
  return list.map((r) => ({ id: r.id as string, name: (r.name as string) ?? '', order: Number(r.sort_order ?? 0) }));
}

/** Names you've lent to before, most recent first (Lend it…'s suggestions on the book page). */
export async function loadLentNames(): Promise<string[]> {
  const db = supabase;
  if (!db) throw new Error('Not connected to a database.');
  const list = await rows(db.from('copy_private').select('lent_to, lent_on').not('lent_to', 'is', null).order('lent_on', { ascending: false }).limit(50));
  const seen = new Map<string, string>();
  for (const r of list) {
    const n = String(r.lent_to);
    if (!seen.has(n.toLowerCase())) seen.set(n.toLowerCase(), n);
  }
  return [...seen.values()].slice(0, 6);
}
